import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/api/apiClient';
import { RefreshIcon } from '../../shared/icons';
import type { B2BClientDto, B2BClientWithApiKeyDto, CreateB2BClientRequest, UpdateB2BClientRequest } from '../../types';
import '../UserManagement/styles.css';
import './styles.css';

const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ru-RU');
};

const B2BClientModal: React.FC<{
    client?: B2BClientDto;
    isOpen: boolean;
    mode: 'create' | 'edit';
    onClose: () => void;
    onSave: (data: CreateB2BClientRequest | UpdateB2BClientRequest) => Promise<void>;
}> = ({ client, isOpen, mode, onClose, onSave }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setName(client?.name || '');
        setDescription(client?.description || '');
    }, [client, isOpen]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isCreateMode = mode === 'create';

    const handleSave = async () => {
        if (!name.trim()) return;

        setLoading(true);
        try {
            await onSave({ name: name.trim(), description: description.trim() || undefined });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="user-avatar mr-3">
                                {name.charAt(0).toUpperCase() || 'B'}
                            </div>
                            <div>
                                <h3 className="modal-title-custom">
                                    {isCreateMode ? 'Создание B2B клиента' : 'Редактирование B2B клиента'}
                                </h3>
                                <p className="modal-subtitle-custom">Управление доступом к B2B каталогу</p>
                            </div>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose} disabled={loading}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    <div className="modal-body-custom">
                        <div className="form-section">
                            <div className="form-grid">
                                <div className="form-group-custom">
                                    <label className="form-label-custom">Название</label>
                                    <div className="input-group-custom">
                                        <input
                                            type="text"
                                            className="form-input-custom"
                                            value={name}
                                            onChange={(event) => setName(event.target.value)}
                                            disabled={loading}
                                            maxLength={200}
                                        />
                                    </div>
                                </div>

                                <div className="form-group-custom">
                                    <label className="form-label-custom">Описание</label>
                                    <div className="input-group-custom">
                                        <textarea
                                            className="form-input-custom"
                                            value={description}
                                            onChange={(event) => setDescription(event.target.value)}
                                            disabled={loading}
                                            maxLength={1000}
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose} disabled={loading}>
                            Отмена
                        </button>
                        <button type="button" className="btn-primary-custom" onClick={handleSave} disabled={loading || !name.trim()}>
                            {loading ? <span className="btn-spinner"></span> : <i className="bi bi-check-lg"></i>}
                            {isCreateMode ? 'Создать' : 'Сохранить'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ApiKeyModal: React.FC<{
    apiKey: string | null;
    clientName: string;
    onClose: () => void;
}> = ({ apiKey, clientName, onClose }) => {
    if (!apiKey) return null;

    const copyKey = async () => {
        await navigator.clipboard.writeText(apiKey);
        toast.success('Ключ скопирован');
    };

    return (
        <div className="modal-backdrop-custom" onClick={(event) => event.target === event.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div>
                            <h3 className="modal-title-custom">API ключ создан</h3>
                            <p className="modal-subtitle-custom">{clientName}</p>
                        </div>
                        <button type="button" className="btn-close-custom" onClick={onClose}>
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div className="modal-body-custom">
                        <div className="form-group-custom">
                            <label className="form-label-custom">Ключ показывается только один раз</label>
                            <div className="input-group-custom">
                                <textarea className="form-input-custom" value={apiKey} readOnly rows={4} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer-custom">
                        <button type="button" className="btn-secondary-custom" onClick={onClose}>
                            Закрыть
                        </button>
                        <button type="button" className="btn-primary-custom" onClick={copyKey}>
                            <i className="bi bi-copy"></i>
                            Скопировать
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const B2BClientManagement: React.FC = () => {
    const [clients, setClients] = useState<B2BClientDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
    const [selectedClient, setSelectedClient] = useState<B2BClientDto | null>(null);
    const [generatedKey, setGeneratedKey] = useState<string | null>(null);
    const [generatedKeyClientName, setGeneratedKeyClientName] = useState('');

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await api.get<{ clients: B2BClientDto[] }>('/api/admin/b2b-clients');
            setClients(result.data.clients || []);
        } catch (err) {
            setError('Произошла ошибка при загрузке B2B клиентов');
            console.error('Error loading B2B clients:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredClients = useMemo(() => {
        const normalizedSearch = searchTerm.toLowerCase();
        return clients.filter(client =>
            !normalizedSearch
            || client.name.toLowerCase().includes(normalizedSearch)
            || (client.description || '').toLowerCase().includes(normalizedSearch));
    }, [clients, searchTerm]);

    const openCreateModal = () => {
        setSelectedClient(null);
        setModalMode('create');
    };

    const openEditModal = (client: B2BClientDto) => {
        setSelectedClient(client);
        setModalMode('edit');
    };

    const closeModal = () => {
        setModalMode(null);
        setSelectedClient(null);
    };

    const showGeneratedKey = (response: B2BClientWithApiKeyDto) => {
        setGeneratedKey(response.apiKey);
        setGeneratedKeyClientName(response.client.name);
    };

    const handleSave = async (data: CreateB2BClientRequest | UpdateB2BClientRequest) => {
        if (modalMode === 'create') {
            const result = await api.post<B2BClientWithApiKeyDto>('/api/admin/b2b-clients', data);
            toast.success('B2B клиент создан');
            showGeneratedKey(result.data);
        } else if (selectedClient) {
            await api.put<B2BClientDto>(`/api/admin/b2b-clients/${selectedClient.id}`, data);
            toast.success('B2B клиент обновлен');
        }

        await loadClients();
    };

    const regenerateKey = async (client: B2BClientDto) => {
        if (!confirm(`Сгенерировать новый ключ для "${client.name}"?\n\nСтарый ключ сразу перестанет работать.`)) {
            return;
        }

        const result = await api.post<B2BClientWithApiKeyDto>(`/api/admin/b2b-clients/${client.id}/regenerate-key`);
        toast.success('Ключ обновлен');
        showGeneratedKey(result.data);
        await loadClients();
    };

    const toggleStatus = async (client: B2BClientDto) => {
        const action = client.isActive ? 'отключить' : 'включить';
        if (!confirm(`Вы действительно хотите ${action} B2B клиента "${client.name}"?`)) {
            return;
        }

        await api.post<B2BClientDto>(`/api/admin/b2b-clients/${client.id}/toggle-status`);
        toast.success(client.isActive ? 'B2B клиент отключен' : 'B2B клиент включен');
        await loadClients();
    };

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5 className="alert-heading">Ошибка</h5>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={loadClients}>Повторить загрузку</button>
            </div>
        );
    }

    return (
        <div className="user-management container-fluid">
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>B2B клиенты</h3>

                    <div className="filter-controls-right">
                        <div className="search-input-compact">
                            <i className="bi bi-search search-icon"></i>
                            <input
                                type="text"
                                className="form-control-compact"
                                placeholder="Поиск..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')} title="Очистить поиск">
                                    <i className="bi bi-x"></i>
                                </button>
                            )}
                        </div>

                        <button className="btn-compact refresh" onClick={loadClients} disabled={loading} title="Обновить">
                            {loading ? <span className="btn-spinner"></span> : <RefreshIcon />}
                        </button>

                        <button className="btn-compact primary" onClick={openCreateModal}>
                            <i className="bi bi-plus-lg"></i>
                            <span>Создать</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="b2b-swagger-link-row">
                <a
                    className="btn-compact"
                    href="/swagger/b2b-catalog"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <i className="bi bi-box-arrow-up-right"></i>
                    <span>Документация B2B API</span>
                </a>
            </div>

            <div className="user-management-table-container">
                <table className="user-management-table">
                    <thead>
                        <tr>
                            <th>Название</th>
                            <th>Статус</th>
                            <th>Ключ</th>
                            <th>Создан</th>
                            <th>Последнее использование</th>
                            <th className="actions-column"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredClients.map(client => (
                            <tr key={client.id}>
                                <td>
                                    <strong>{client.name}</strong>
                                    {client.description && <div className="text-muted small">{client.description}</div>}
                                </td>
                                <td>
                                    <span className={`badge ${client.isActive ? 'bg-success' : 'bg-secondary'}`}>
                                        {client.isActive ? 'Активен' : 'Отключен'}
                                    </span>
                                </td>
                                <td>
                                    <code>{client.apiKeyPrefix}_...{client.apiKeySuffix}</code>
                                </td>
                                <td>{formatDateTime(client.createdAt)}</td>
                                <td>{formatDateTime(client.lastUsedAt)}</td>
                                <td className="actions-column">
                                    <div className="btn-group btn-group-sm">
                                        <button className="btn btn-small btn-edit" onClick={() => openEditModal(client)} title="Редактировать">
                                            <i className="bi bi-pencil"></i>
                                        </button>
                                        <button className="btn btn-small btn-edit" onClick={() => regenerateKey(client)} title="Сгенерировать ключ">
                                            <i className="bi bi-key"></i>
                                        </button>
                                        <button
                                            className={`btn btn-small ${client.isActive ? 'btn-delete' : 'btn-edit'}`}
                                            onClick={() => toggleStatus(client)}
                                            title={client.isActive ? 'Отключить' : 'Включить'}
                                        >
                                            <i className={`bi ${client.isActive ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredClients.length === 0 && (
                <div className="text-center py-4">
                    <p className="text-muted">B2B клиенты не найдены</p>
                </div>
            )}

            <div className="mt-3 text-muted">
                Показано {filteredClients.length} из {clients.length} B2B клиентов
            </div>

            <B2BClientModal
                client={selectedClient || undefined}
                isOpen={modalMode !== null}
                mode={modalMode || 'create'}
                onClose={closeModal}
                onSave={handleSave}
            />

            <ApiKeyModal
                apiKey={generatedKey}
                clientName={generatedKeyClientName}
                onClose={() => setGeneratedKey(null)}
            />
        </div>
    );
};

export default B2BClientManagement;
